import { all, call, put, race, select, takeLatest } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { actions } from '../../constants';
import Schemas from '../models';
import ApiHelper from '../../api/ApiHelper';
import { isRefreshingToken } from '../selectors';

function* login(action) {
  try {
    const tokens = yield call(ApiHelper.post, 'oauth/token', {
      grant_type: 'password',
      client_id: process.env.REACT_APP_CLIENT_ID,
      client_secret: process.env.REACT_APP_CLIENT_SECRET,
      username: action.payload.username,
      password: action.payload.password
    });

    saveTokensStorage(tokens);

    yield call(getUserMe);
    const { error } = yield race({
      success: actions.LOAD_USER_ME_SUCCESS,
      error: action.LOAD_USER_ME_FAIL
    });

    if (!error) yield put({ type: actions.LOGIN_SUCCESS });
    else yield put({ type: actions.LOGIN_FAIL, payload: error });
  } catch (error) {
    yield put({ type: actions.LOGIN_FAIL, payload: error });
  }
}

function* loginUserWithFacebook(accessToken) {
  try {
    const tokens = yield call(ApiHelper.post, 'oauth/token', {
      grant_type: 'social',
      network: 'facebook',
      client_id: process.env.REACT_APP_CLIENT_ID,
      client_secret: process.env.REACT_APP_CLIENT_SECRET,
      access_token: accessToken
    });

    saveTokensStorage(tokens);

    yield put({ type: actions.LOGIN_WITH_FACEBOOK_SUCCESS });
  } catch (error) {
    if (error.statusCode === 401)
      yield put({
        type: actions.LOGIN_WITH_FACEBOOK_NO_ACCOUNT,
        payload: error,
        accessToken
      });
    else yield put({ type: actions.LOGIN_WITH_FACEBOOK_FAIL, payload: error });
  }
}

function* logout(action) {
  yield call(clear, err => {});

  yield put({ type: actions.LOGOUT_SUCCESS });
}

function* getUserMe() {
  const onSuccess = function*(response) {
    yield put({ type: actions.LOAD_USER_ME_SUCCESS, payload: response });
  };

  const onError = function*(error) {
    yield put({ type: actions.LOAD_USER_ME_FAIL, payload: error });

    if (error.statusCode === 401) yield call(logout);
  };

  yield call(fetchListener, {
    url: 'api/me',
    method: 'GET',
    schema: Schemas.USER,
    onSuccess: onSuccess,
    onError: onError
  });
}

export function* fetchListener(action) {
  if (action.noAuth) {
    yield call(makeAuthenticatedRequest, action);
  } else {
    const token = yield call(getItem, 'access_token');

    if (token) {
      const shouldRefresh = yield call(needRefresh);

      if (!shouldRefresh) {
        yield call(makeAuthenticatedRequest, action);
      }

      if (shouldRefresh) {
        const error = yield call(refreshTokens);
        if (!error) {
          yield delay(50);
          yield call(makeAuthenticatedRequest, action);
        } else {
          yield call(logout, true);
        }
      }
    } else {
      yield call(action.onError, {
        statusCode: 401,
        type: actions.TOKEN_REFRESH_FAIL
      });
    }
  }
}

function getItem(key) {
  return localStorage.getItem(key);
}

function clear() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('expires_in');
}

function* needRefresh() {
  const expiresIn = yield call(getItem, 'expires_in');
  return Date.now() >= expiresIn;
}

function* refreshTokens() {
  const isRefreshing = yield select(isRefreshingToken);

  if (isRefreshing) {
    const { error } = yield race({
      success: actions.TOKEN_REFRESH_SUCCESS,
      error: actions.TOKEN_REFRESH_FAIL
    });

    return error;
  }

  yield put({ type: actions.TOKEN_REFRESH });

  try {
    const refreshToken = yield call(getItem, 'refresh_token');
    const tokens = yield call(ApiHelper.post, 'oauth/token', {
      grant_type: 'refresh_token',
      client_id: process.env.REACT_APP_CLIENT_ID,
      client_secret: process.env.REACT_APP_CLIENT_SECRET,
      refresh_token: refreshToken
    });

    saveTokensStorage(tokens);

    yield put({ type: actions.TOKEN_REFRESH_SUCCESS });
    return null;
  } catch (error) {
    yield put({ type: actions.TOKEN_REFRESH_FAIL, payload: error });

    if (!error.statusCode) return null;

    return error;
  }
}

function* makeAuthenticatedRequest(action, accessToken) {
  const isAccessExpired = error => error.statusCode && error.statusCode === 401;

  const access_token = action.noAuth
    ? null
    : yield call(getItem, 'access_token');

  try {
    if (!action.photo) {
      const response = yield call(
        ApiHelper.submit,
        action.url,
        action.method,
        JSON.stringify(action.data),
        action.schema,
        accessToken || access_token
      );
      yield action.onSuccess(response);
    } else {
      action.data['_method'] = 'PUT';
      const response = yield call(
        ApiHelper.submit,
        action.url,
        'POST',
        JSON.stringify(action.data),
        action.schema,
        accessToken || access_token,
        'photo',
        action.data.photo
      );
      yield action.onSuccess(response);
    }
  } catch (error) {
    if (action.noAuth) {
      yield action.onError(error);
    } else {
      if (isAccessExpired(error)) {
        const refreshError = yield call(refreshTokens);

        if (!refreshError) {
          yield makeAuthenticatedRequest(action);
        }
      } else {
        yield action.onError(error);
      }
    }
  }
}

function saveTokensStorage(tokens) {
  localStorage.setItem('access_token', tokens.access_token);
  localStorage.setItem('refresh_token', tokens.refresh_token);
  let expiresAtTime = new Date().getTime();
  let expiresMillisecond = (Number(tokens.expires_in) - 60) * 1000;
  let expiresAt = new Date(expiresAtTime + expiresMillisecond);
  localStorage.setItem('expires_in', expiresAt.getTime().toString());
}

export default function* auth() {
  yield all([
    yield takeLatest(actions.LOGIN, login),
    yield takeLatest(actions.LOGIN_WITH_FACEBOOK, loginUserWithFacebook),
    yield takeLatest(actions.LOAD_USER_ME, getUserMe),
    yield takeLatest(actions.LOGOUT, logout)
  ]);
}



// WEBPACK FOOTER //
// ./src/redux/sagas/auth.js